// Data seam for ARCO privacy requests.
//
// Two call sites, one shape, same as consent-data.ts: the operator works a
// client's compliance file cross-tenant, the clinic works their own. The API
// enforces which is which; the UI picks the pair of functions matching where
// it is mounted.

import { apiClient } from '../../api/client.js'

/** The four LFPDPPP rights a request can invoke; values match the API/DB enum. */
export const ARCO_RIGHTS = ['acceso', 'rectificacion', 'cancelacion', 'oposicion'] as const
export type ArcoRight = (typeof ARCO_RIGHTS)[number]

/** A request's lifecycle states, mirroring the API/DB enum (fulfilled/refused/expired are closed). */
export const ARCO_STATES = [
  'received',
  'awaiting_identity',
  'routed_to_tenant',
  'in_progress',
  'fulfilled',
  'refused',
  'expired',
] as const
export type ArcoState = (typeof ARCO_STATES)[number]

export type ReceivedVia = 'email' | 'chat' | 'phone' | 'other'

export interface PrivacyRequest {
  id: string
  rights: ArcoRight[]
  /** Whatever the person gave us to find themselves by. Personal data. */
  subjectReference: string
  subjectNote: string | null
  receivedVia: string
  state: ArcoState
  contactId: string | null
  /** Standing objections of the linked person. Empty when nobody is linked. */
  contactOpposition: OppositionKind[]
  receivedAt: string
  /** 20 business days from arrival. The promise the aviso makes. */
  respondBy: string
  /** 15 business days from the response. Null until we have answered. */
  executeBy: string | null
  respondedAt: string | null
  executedAt: string | null
  resolution: string | null
  createdAt: string
  /** Closed requests show no deadline. */
  closed: boolean
}

export interface PrivacyRequestList {
  requests: PrivacyRequest[]
  total: number
  /** Open across all states, so the tab can carry a count. */
  open: number
}

export interface CreatePrivacyRequest {
  rights: ArcoRight[]
  subjectReference: string
  subjectNote?: string
  receivedVia?: ReceivedVia
  /** ISO. Omit for "arrived now". */
  receivedAt?: string
}

/** The standing objections a contact can hold. Every value has a real enforcement point (see OPPOSITION_LABEL). */
export const OPPOSITION_KINDS = ['automated_replies', 'analytics'] as const
export type OppositionKind = (typeof OPPOSITION_KINDS)[number]

/**
 * What each objection means, and what it actually does. Both are enforced:
 * `automated_replies` in the inbound pipeline, `analytics` in the reports.
 * Nothing goes on this list without an enforcement point — an objection
 * nobody honours is worse than none, because the record says otherwise.
 */
export const OPPOSITION_LABEL: Record<OppositionKind, string> = {
  automated_replies: 'No quiere que le responda el agente',
  analytics: 'No quiere aparecer en los reportes',
}

export const OPPOSITION_HELP: Record<OppositionKind, string> = {
  automated_replies:
    'Sus mensajes pasan directo a una persona del equipo. El agente no le contesta.',
  analytics: 'Sus conversaciones y prospectos no se cuentan en los reportes del negocio.',
}

export interface UpdatePrivacyRequest {
  state?: ArcoState
  contactId?: string | null
  resolution?: string | null
  subjectNote?: string | null
  markResponded?: boolean
  markExecuted?: boolean
  /** Replaces the whole set on the linked contact. */
  opposition?: OppositionKind[]
}

/** 'abiertas' is the working queue; 'todas' includes closed ones. */
export type StateFilter = 'abiertas' | 'todas' | ArcoState

// ── One surface: the operator console ───────────────────────────────────────
//
// There are no clinic-scoped calls. ARCO requests arrive at
// privacidad@inherta.com, which only INHERTA can read, so only INHERTA can act
// on one — the endpoints behind these are operator-only too.

/** GET /admin/tenants/:id/privacy-requests?state= — a client's ARCO file, filtered ('abiertas' = working queue). */
export async function getTenantPrivacyRequests(
  tenantId: string,
  filter: StateFilter,
): Promise<PrivacyRequestList> {
  const res = await apiClient.get<{ data: PrivacyRequestList }>(
    `/admin/tenants/${tenantId}/privacy-requests?state=${filter}`,
  )
  return res.data.data
}

/**
 * POST /admin/tenants/:id/privacy-requests — logs an arrived request; the
 * server stamps respondBy (20 business days). Omitted receivedAt = now.
 */
export async function createTenantPrivacyRequest(
  tenantId: string,
  body: CreatePrivacyRequest,
): Promise<PrivacyRequest> {
  const res = await apiClient.post<{ data: PrivacyRequest }>(
    `/admin/tenants/${tenantId}/privacy-requests`,
    body,
  )
  return res.data.data
}

/**
 * PATCH /admin/tenants/:t/privacy-requests/:id — state moves, contact linking,
 * respond/execute stamps; `opposition` replaces the linked contact's whole set.
 */
export async function updateTenantPrivacyRequest(
  tenantId: string,
  id: string,
  patch: UpdatePrivacyRequest,
): Promise<PrivacyRequest> {
  const res = await apiClient.patch<{ data: PrivacyRequest }>(
    `/admin/tenants/${tenantId}/privacy-requests/${id}`,
    patch,
  )
  return res.data.data
}

// ── Display ─────────────────────────────────────────────────────────────────

export const RIGHT_LABEL: Record<ArcoRight, string> = {
  acceso: 'Acceso',
  rectificacion: 'Rectificación',
  cancelacion: 'Cancelación',
  oposicion: 'Oposición',
}

/** What each right means, in the words a person would use asking for it. */
export const RIGHT_HELP: Record<ArcoRight, string> = {
  acceso: 'Quiere saber qué datos suyos tienes.',
  rectificacion: 'Quiere corregir un dato equivocado.',
  cancelacion: 'Quiere que elimines sus datos.',
  oposicion: 'No quiere que uses sus datos para algo en particular.',
}

export const STATE_LABEL: Record<ArcoState, string> = {
  received: 'Recibida',
  awaiting_identity: 'Falta identificación',
  routed_to_tenant: 'Turnada a la clínica',
  in_progress: 'En proceso',
  fulfilled: 'Atendida',
  refused: 'Improcedente',
  expired: 'Vencida sin identificación',
}

export const VIA_LABEL: Record<string, string> = {
  email: 'Correo',
  chat: 'Chat',
  phone: 'Teléfono',
  other: 'Otro',
}

/**
 * How a deadline reads. Silent until it matters, then loud — the same shape the
 * prospects board uses for a cold card ("N días sin actividad").
 *
 * Calendar days, not business days, on purpose: the person reading this wants
 * to know how much real time is left, and "3 días" meaning five because of a
 * weekend is a nasty surprise. The DEADLINE is business days; the countdown to
 * it is measured in the days someone actually lives through.
 */
export function deadlineText(
  iso: string,
  now: Date = new Date(),
): { text: string; tone: 'neutral' | 'warning' | 'danger' } {
  // Counted between CALENDAR days, not by dividing elapsed milliseconds.
  // A deadline three days and one second ago is "hace 3 días" to a person,
  // but flooring the raw difference calls it four; and "vence hoy" means the
  // due date is today's date, not that fewer than 24 hours remain. Rounding
  // the day difference also absorbs the 23- and 25-hour days a DST change
  // produces, which a fixed 86 400 000 divisor does not.
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const due = new Date(iso)
  const days = Math.round((startOfDay(due) - startOfDay(now)) / (24 * 60 * 60 * 1000))

  if (days < 0) {
    const late = Math.abs(days)
    return { text: late === 1 ? 'Vencida hace 1 día' : `Vencida hace ${late} días`, tone: 'danger' }
  }
  if (days === 0) return { text: 'Vence hoy', tone: 'danger' }
  if (days === 1) return { text: 'Vence mañana', tone: 'danger' }
  if (days <= 5) return { text: `Faltan ${days} días`, tone: 'warning' }
  return { text: `Faltan ${days} días`, tone: 'neutral' }
}

/** `2026-08-08T15:00:00Z` → `8 ago 2026`. */
export function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}
